import React, { useContext, useEffect, useMemo, useState } from "react";
import { getAllGame } from "../../api/user";
import CardGridSkeleton from "../../components/Skeleton/CardGridSkeleton";
import { GameContext } from "../../store/game/GameContext";
import Welcome from "./Welcome";
import { useOutletContext } from "react-router";
import PublicLeagueRow, { PANEL_BLEED } from "./landing/PublicLeagueRow";
import GameFiltersBar, { NEUTRAL_FILTERS } from "./landing/GameFiltersBar";
import { applyAllFilters, applySort } from "./landing/gameFilterPredicates";

// The /contest/public-leagues route opens with the League Type pre-set to
// "public" (so users land where the View All link implied). Clear filters
// resets everything — including the type — back to neutral so the bar can
// browse across all league types.
const INITIAL_FILTERS = { ...NEUTRAL_FILTERS, type: "public" };

const FindGames = () => {
  const { fetching, setFetching } = useContext(GameContext);
  const { search } = useOutletContext(); // title/description search from Contest.jsx
  const [games, setGames] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [sort, setSort] = useState("starting-soon");

  const fetchGame = async (controller) => {
    try {
      setFetching(true);
      const response = await getAllGame({ signal: controller.signal });
      if (response?.status === 200) {
        setGames(response.data.data || []);
      }
    } catch (error) {
      if (error.name !== "AbortError") console.error(error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchGame(controller);
    return () => {
      localStorage.removeItem("game_type");
      localStorage.removeItem("isAuthenticated");
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Client, routing pass: "Private leagues show only in 'My Leagues'." Find
  // Games browses what anyone can walk up to, and get-all-game returns every
  // active league regardless of type — so a private league was surfacing on a
  // browse screen where its own invite gate is the only thing that would have
  // stopped the visitor. Public Leagues and League Play home already apply the
  // same filter; this screen was the one that did not.
  const browsable = useMemo(
    () => (games || []).filter((g) => g?.game_type !== "private"),
    [games]
  );

  const searchedGames = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return browsable;
    return browsable.filter(
      (g) =>
        (g.title && g.title.toLowerCase().includes(q)) ||
        (g.description && g.description.toLowerCase().includes(q))
    );
  }, [browsable, search]);

  const filteredGames = useMemo(
    () => applyAllFilters(searchedGames, filters),
    [searchedGames, filters]
  );

  const sortedGames = useMemo(
    () => applySort(filteredGames, sort),
    [filteredGames, sort]
  );

  const handleClear = () => {
    setFilters(NEUTRAL_FILTERS);
    setSort("starting-soon");
  };

  return (
    <div className="px-4 md:px-8 pt-2 md:pt-4 pb-12">
      <div className="max-w-screen-xl mx-auto">
        <GameFiltersBar
          filters={filters}
          onChange={setFilters}
          sort={sort}
          onSortChange={setSort}
          onClear={handleClear}
          totalCount={games.length}
          filteredCount={sortedGames.length}
        />

        <div className="mt-5">
          {/* Filters → sortedGames → PublicLeagueRow, the same preview card the
              rest of the site uses. The skeleton keeps the old grid geometry
              only while loading; it is replaced by the row list the moment the
              leagues land. */}
          {fetching ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <CardGridSkeleton cards={8} />
            </div>
          ) : games.length === 0 && !search ? (
            <Welcome />
          ) : sortedGames.length === 0 ? (
            <p className="text-sm text-[#66748C] dm-sans text-center py-10">
              No leagues match these filters. Try clearing one.
            </p>
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-white px-4 md:px-5">
              <div className="divide-y divide-divider">
                {sortedGames.map((game) => (
                  <PublicLeagueRow key={game.id} game={game} bleed={PANEL_BLEED.inset} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FindGames;
